import React from "react";
import { craftsmanshipImages } from "../data/mockData";
import { Sparkles, Clock, Hand, Heart } from "lucide-react";

export const Craftsmanship = () => {
  const process = [
    {
      icon: <Heart className="text-[#C4B5A0]" size={36} />,
      title: "Consultation",
      description: "We begin with understanding your vision, measurements, and the story you want your piece to tell."
    },
    {
      icon: <Sparkles className="text-[#C4B5A0]" size={36} />,
      title: "Design",
      description: "Our designers create custom sketches and select the finest fabrics and embellishments for your piece."
    },
    {
      icon: <Hand className="text-[#C4B5A0]" size={36} />,
      title: "Handcrafting",
      description: "Every stitch, every bead, every detail is meticulously handcrafted by our master artisans."
    },
    {
      icon: <Clock className="text-[#C4B5A0]" size={36} />,
      title: "Perfection",
      description: "Multiple fittings ensure your piece fits perfectly and exceeds your expectations in every way."
    }
  ];

  const techniques = [
    {
      name: "Hand Embroidery",
      description: "Traditional Nigerian embroidery techniques combined with European haute couture methods"
    },
    {
      name: "Beadwork & Embellishments",
      description: "Each bead and crystal is hand-placed to create intricate, one-of-a-kind patterns"
    },
    {
      name: "Draping & Construction",
      description: "Classical draping techniques to create flowing, flattering silhouettes"
    },
    {
      name: "Fabric Selection",
      description: "Only the finest silks, satins, laces, and meshes from around the world"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-[70vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1621781727164-8121aec35c88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxzZXdpbmclMjBjcmFmdHNtYW5zaGlwfGVufDB8fHx8MTc3MjQ2NTExNnww&ixlib=rb-4.1.0&q=85"
          alt="MIA Atelier Craftsmanship"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/80" />
        <div className="absolute inset-0 flex items-center justify-center text-center px-6">
          <div className="max-w-3xl text-white">
            <h1 className="text-6xl md:text-7xl mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Craftsmanship
            </h1>
            <p className="text-xl tracking-wider">
              Where art meets tradition, and love meets legacy
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24 px-6 bg-[#FAF9F7]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            The Art of Couture
          </h2>
          <p className="text-[#2C2C2C] text-lg leading-relaxed mb-6">
            At MIA Atelier, craftsmanship is not just a skill—it's a sacred tradition passed down 
            through generations. Every piece that leaves our atelier represents hundreds of hours 
            of meticulous handwork, combining traditional Nigerian textile arts with European 
            haute couture techniques.
          </p>
          <p className="text-[#8B7D6B] text-lg leading-relaxed">
            We believe that true luxury lies not in mass production, but in the careful attention 
            to detail that only hand craftsmanship can provide.
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Our Process
            </h2>
            <p className="text-[#8B7D6B] tracking-wide">
              From concept to completion
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#F5F3F0] mb-6">
                  {step.icon}
                </div>
                <div className="text-4xl text-[#C4B5A0] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <h3 className="text-2xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {step.title}
                </h3>
                <p className="text-[#8B7D6B] leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Atelier Gallery */}
      <section className="py-24 px-6 bg-[#F5F3F0]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Inside Our Atelier
            </h2>
            <p className="text-[#8B7D6B] tracking-wide">
              A glimpse into where magic happens
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {craftsmanshipImages.map((item) => (
              <div key={item.id} className="group overflow-hidden rounded-lg shadow-lg">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                    <div className="text-white">
                      <h3 className="text-xl mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-300">{item.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Techniques */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Our Techniques
            </h2>
            <p className="text-[#8B7D6B] tracking-wide">
              Master craftsmanship in every detail
            </p>
          </div>

          <div className="space-y-8">
            {techniques.map((technique, index) => (
              <div
                key={index}
                className="flex items-start space-x-6 p-8 bg-[#FAF9F7] rounded-lg hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-[#C4B5A0] flex items-center justify-center text-white text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {technique.name}
                  </h3>
                  <p className="text-[#8B7D6B] leading-relaxed">{technique.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quality Promise */}
      <section className="py-24 px-6 bg-[#C4B5A0]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl mb-8 text-[#2C2C2C]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Our Promise
          </h2>
          <p className="text-[#2C2C2C] text-xl leading-relaxed mb-6">
            Every piece that bears the MIA Atelier name is guaranteed to be:
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="bg-white/20 p-6 rounded-lg backdrop-blur-sm">
              <h3 className="text-2xl mb-3 text-[#2C2C2C]" style={{ fontFamily: "'Playfair Display', serif" }}>
                100% Handmade
              </h3>
              <p className="text-[#6B4E37]">No machines, only skilled hands and loving care</p>
            </div>
            <div className="bg-white/20 p-6 rounded-lg backdrop-blur-sm">
              <h3 className="text-2xl mb-3 text-[#2C2C2C]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Premium Materials
              </h3>
              <p className="text-[#6B4E37]">Only the finest fabrics and embellishments</p>
            </div>
            <div className="bg-white/20 p-6 rounded-lg backdrop-blur-sm">
              <h3 className="text-2xl mb-3 text-[#2C2C2C]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Timeless Quality
              </h3>
              <p className="text-[#6B4E37]">Built to last generations, not seasons</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
